import { create } from 'zustand'

/**
 * Which pages the automatic visual review has already taken.
 *
 * This store used to also carry a capture viewport, because the review took
 * its two screenshots by re-laying-out the frame in front of the creator —
 * first their own `activeBreakpoint`, then, after that was found visible, the
 * frame's. Both were the thing on screen. The shots now come from clones in
 * offscreen hosts, so no viewport is shared with anything and the only thing
 * worth remembering is which pages are done.
 */
interface PreviewReviewClaimState {
  /**
   * Pages already reviewed, keyed `<slug>:<pageId>`.
   *
   * This used to be a `useRef` on the bridge, so it lived only as long as the
   * mount — and the bridge unmounts when the working view switches to the
   * report and when the creator leaves the preview rail. A 30-page app walked,
   * viewed as a report, and walked again cost 60 calls for 30 distinct pages.
   *
   * Keyed by slug as well as page so another project's page 1 is still its own
   * page and still gets its one review.
   */
  reviewedPages: ReadonlySet<string>
  /** Records a page as reviewed; returns false when it already was. */
  claimReview: (reviewKey: string) => boolean
}

export const usePreviewReviewClaimStore = create<PreviewReviewClaimState>((set, get) => ({
  reviewedPages: new Set<string>(),
  claimReview: (reviewKey) => {
    if (get().reviewedPages.has(reviewKey)) return false
    set((state) => ({ reviewedPages: new Set(state.reviewedPages).add(reviewKey) }))
    return true
  },
}))
